import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getAPIEndpoint } from './utils/api';
import './LeadCRM.css';

// Mock data for development when API is unavailable
const MOCK_LEADS = {
  get: [
    { 
      id: 659,
      age: 23,
      name: 'Liam Jacob',
      role: 'seller',
      type: 'get',
      email: '',
      gender: 'Male',
      company: 'Bethel',
      user_id: 497,
      teams_id: 265,
      id_number: '98357826724665',
      created_at: 1745570510724,
      profile_info: '# Liam Jacob\'s Profile\n## Contact Information\n- **Address:** 9 Gretel Street\n- **Contact Number:** +27 72 723 5786\n## Property Details\n- **Selling Price:** Not provided\n- **Status:** Own but selling\n## Engagement Analysis\n- **Interest Level:** Actively interested in selling his property. Awaiting additional details regarding expected selling price and email contact.\n## Next Steps\n- Gather expected selling price and email address to complete the profile. Consider scheduling a meeting for further discussion.',
      recover_date: null,
      contact_number: '+27727235786',
      pipeline_status_id: 649,
      sellers_property_value: '',
      buyer_property_viewings: [0],
      pipeline_status_details: { 
        title: 'Lead Generation', 
        hex: { 
          text: '#00008B', 
          background: '#DDEEFF' 
        },
        description: 'Initial contact established' 
      },
      seller_property_address: '9 Gretel Street',
      status: 'New',
      source: 'Website',
      notes: 'Interested in property in central area',
      industry: 'Real Estate',
      value: 250000
    },
    { 
      id: 660,
      age: 34,
      name: 'Sarah Brown',
      role: 'buyer',
      type: 'get',
      email: 'sarah@example.com',
      gender: 'Female',
      company: 'XYZ Inc',
      user_id: 497,
      teams_id: 265,
      id_number: '84215763984512',
      created_at: 1745570510724,
      profile_info: '# Sarah Brown\'s Profile\n## Contact Information\n- **Address:** 15 Oak Avenue\n- **Contact Number:** +27 82 456 7890\n## Property Details\n- **Budget:** R450,000\n- **Status:** Looking to buy\n## Engagement Analysis\n- **Interest Level:** Serious buyer looking for investment properties.\n## Next Steps\n- Schedule property viewings in preferred areas.',
      recover_date: null,
      contact_number: '+27824567890',
      pipeline_status_id: 650,
      sellers_property_value: '',
      buyer_property_viewings: ['123 Main St', '456 Park Ave'],
      pipeline_status_details: { 
        title: 'Initial Consultation', 
        hex: { 
          text: '#006400', 
          background: '#E6FFE6' 
        },
        description: 'First consultation completed' 
      },
      seller_property_address: '',
      status: 'Contacted',
      source: 'Referral',
      notes: 'Looking for investment property',
      industry: 'Finance',
      value: 450000,
      position: 'Director'
    }
  ],
  keep: [
    { 
      id: 661,
      age: 42,
      name: 'Emma Wilson',
      role: 'seller',
      type: 'keep',
      email: 'emma@example.com',
      gender: 'Female',
      company: 'Wilson Enterprises',
      user_id: 497,
      teams_id: 265,
      id_number: '74125896301254',
      created_at: 1745570510724,
      profile_info: '# Emma Wilson\'s Profile\n## Contact Information\n- **Address:** 28 Maple Road\n- **Contact Number:** +27 83 555 4444\n## Property Details\n- **Selling Price:** R350,000\n- **Status:** Current client, looking for second property\n## Engagement Analysis\n- **Interest Level:** Regular client with strong relationship.\n## Next Steps\n- Discuss new property options and schedule viewings.',
      recover_date: null,
      contact_number: '+27835554444',
      pipeline_status_id: 651,
      sellers_property_value: '350000',
      buyer_property_viewings: [0],
      pipeline_status_details: { 
        title: 'Listing Agreement', 
        hex: { 
          text: '#800080', 
          background: '#F3E6FF' 
        },
        description: 'Agreement signed' 
      },
      seller_property_address: '28 Maple Road',
      status: 'Active',
      source: 'Cold Call',
      notes: 'Regular client, looking for second property',
      industry: 'Technology',
      value: 350000,
      position: 'VP Sales'
    }
  ],
  recover: [
    { 
      id: 662,
      age: 39,
      name: 'Lisa Rodriguez',
      role: 'buyer',
      type: 'recover',
      email: 'lisa@example.com',
      gender: 'Female',
      company: 'Rodriguez Co',
      user_id: 497,
      teams_id: 265,
      id_number: '63254178945213',
      created_at: 1745570510724,
      profile_info: '# Lisa Rodriguez\'s Profile\n## Contact Information\n- **Address:** 42 Pine Street\n- **Contact Number:** +27 84 888 9999\n## Property Details\n- **Budget:** R600,000\n- **Status:** Previous client from 2021\n## Engagement Analysis\n- **Interest Level:** Previously engaged but communication lapsed.\n## Next Steps\n- Reconnect and assess current needs. Follow up needed.',
      recover_date: 1735570510724,
      contact_number: '+27848889999',
      pipeline_status_id: 652,
      sellers_property_value: '',
      buyer_property_viewings: ['789 Beach Blvd', '101 Mountain View'],
      pipeline_status_details: { 
        title: 'Re-Engagement', 
        hex: { 
          text: '#A0522D', 
          background: '#FFEEDD' 
        },
        description: 'Attempting to re-establish relationship' 
      },
      seller_property_address: '',
      status: 'Inactive',
      source: 'Referral',
      notes: 'Previous client from 2021, follow up needed',
      industry: 'Healthcare',
      value: 600000,
      position: 'CFO'
    }
  ]
};

// Three dots menu icon component
const MenuIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="2.5" r="1.5" />
    <circle cx="8" cy="8" r="1.5" />
    <circle cx="8" cy="13.5" r="1.5" />
  </svg>
);

// Custom component for pipeline status dropdown
const PipelineStatusDropdown = ({ options, value, onChange, description }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectedOption = options.find(o => o.id === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="pipeline-dropdown-container" ref={dropdownRef}>
      <div 
        className={`pipeline-dropdown-header ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedOption ? (
          <div className="pipeline-selected-option">
            <span 
              className="pipeline-option-color"
              style={{ 
                backgroundColor: selectedOption.hex?.background || '#F5F5F5',
                borderColor: selectedOption.hex?.text || '#333333'
              }}
            ></span>
            <span>{selectedOption.title}</span>
          </div>
        ) : (
          <div className="pipeline-placeholder">Select Pipeline Status</div>
        )}
        <div className="pipeline-dropdown-arrow"></div>
      </div>
      
      {isOpen && (
        <div className="pipeline-dropdown-options">
          {options.map(option => (
            <div 
              key={option.id}
              className={`pipeline-dropdown-option ${option.id === value ? 'selected' : ''}`}
              onClick={() => {
                onChange(option.id);
                setIsOpen(false);
              }}
            >
              <span 
                className="pipeline-option-color"
                style={{ 
                  backgroundColor: option.hex?.background || '#F5F5F5',
                  borderColor: option.hex?.text || '#333333'
                }}
              ></span>
              <span>{option.title}</span>
            </div>
          ))}
        </div>
      )}
      
      {/* Description section removed for edit view */}
    </div>
  );
};

function ClientCRM({ pageType }) {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadType, setLeadType] = useState(pageType || 'get');
  const [apiEndpoint, setApiEndpoint] = useState('');
  const [typeFilter, setTypeFilter] = useState("seller"); // Default to seller filter
  const [interactions, setInteractions] = useState([]);
  const [loadingInteractions, setLoadingInteractions] = useState(false);
  
  // Lead management menu state
  const [menuOpen, setMenuOpen] = useState(null); // Stores ID of lead with open menu
  const [showMoveAgentModal, setShowMoveAgentModal] = useState(false);
  const [showMoveListModal, setShowMoveListModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMoveConfirmModal, setShowMoveConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [pendingMoveType, setPendingMoveType] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [teamMembersError, setTeamMembersError] = useState(null);
  const [agents, setAgents] = useState([]); // List of agents for transfer
  const [branches, setBranches] = useState([]); // List of branches for transfer
  const [showTeamMemberSelector, setShowTeamMemberSelector] = useState(false);
  const [viewingTeamMemberId, setViewingTeamMemberId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userRoleLevel, setUserRoleLevel] = useState(0); // Track user's role level

  // Add new state variables for edit functionality
  const [isEditing, setIsEditing] = useState(false);
  const [editedLead, setEditedLead] = useState(null);
  
  // Add new state variable for pipeline status options
  const [pipelineStatusOptions, setPipelineStatusOptions] = useState([]);
  const [loadingPipelineStatus, setLoadingPipelineStatus] = useState(false);
  const [saveProcessing, setSaveProcessing] = useState(false);

  // Add new state variables for note management
  const [noteText, setNoteText] = useState('');
  const [selectedNoteType, setSelectedNoteType] = useState('note');
  const [editingNote, setEditingNote] = useState(null);
  const [savingNote, setSavingNote] = useState(false);

  // Add state for tracking delete operation
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [confirmDeleteNote, setConfirmDeleteNote] = useState(false);

  // Add a ref for the note editor section
  const noteEditorRef = useRef(null);

  // Add a new state variable for the bin confirmation
  const [showBinConfirm, setShowBinConfirm] = useState(false);
  const [sendingToBin, setSendingToBin] = useState(false);

  useEffect(() => {
    setApiEndpoint(getAPIEndpoint());
    
    // Get current user ID and role level from localStorage
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    setCurrentUserId(userDetails.id || null);
    
    // Use role instead of role_level - role is the actual field returned by the API
    const roleLevel = Number(userDetails.role) || 1;
    setUserRoleLevel(roleLevel);
    
    console.log('User Details from localStorage:', userDetails);
    console.log('Role Level (parsed from role):', roleLevel);
    console.log('Role Level Type:', typeof roleLevel);
    
    // Even if the dropdown's visible, we'll log this to debug whether it's a render condition or a state issue
    if (roleLevel > 1) {
      console.log('User should see the team selector dropdown');
    } else {
      console.log('User does NOT have permissions to see team selector dropdown');
    }
    
    // Fetch team members when component mounts
    fetchTeamMembers();
    
    // Fetch pipeline status options when component mounts
    fetchPipelineStatusOptions();
  }, []);

  useEffect(() => {
    if (apiEndpoint) {
      fetchLeads();
    }
  }, [apiEndpoint, leadType, viewingTeamMemberId]);

  // Update leadType when pageType changes
  useEffect(() => {
    if (pageType) {
      console.log(`Page type changed to: ${pageType}`);
      setLeadType(pageType);
      // Reset selected lead when changing page types
      setSelectedLead(null);
      setInteractions([]);
    }
  }, [pageType]);

  // Fetch interactions when a lead is selected
  useEffect(() => {
    if (selectedLead && selectedLead.id && apiEndpoint) {
      console.log(`Fetching interactions for selected lead: ${selectedLead.id}`);
      fetchInteractions(selectedLead.id);
    } else {
      // Clear interactions when no lead is selected
      console.log('No lead selected or API endpoint not available, clearing interactions');
      setInteractions([]);
      setLoadingInteractions(false);
    }
  }, [selectedLead?.id, apiEndpoint]); // Only re-run when lead ID or API endpoint changes

  const fetchLeads = async () => {
    setLoading(true);
    setLeads([]); // Clear current leads while loading
    setSelectedLead(null); // Clear selected lead
    
    try {
      // Get user details from localStorage
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const userId = viewingTeamMemberId || userDetails.id || 483; // Use selected team member or current user
      const authToken = localStorage.getItem('authToken');
      
      // Show which team member's leads we're loading
      let memberName = 'your';
      if (viewingTeamMemberId) {
        const member = teamMembers.find(m => m.id === viewingTeamMemberId);
        if (member) {
          memberName = `${member.first_name || member.name}'s`;
        } else {
          memberName = 'team member\'s';
        }
      }
      
      // Updated URL structure to match the LeadCRM component
      const url = `${apiEndpoint}/clients?user_id=${userId}&type=${leadType}`;
      
      console.log(`Attempting to fetch ${memberName} leads from:`, url);
      
      const response = await axios.get(url, {
        timeout: 5000,
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });
      
      setLeads(response.data);
      
      // Set the first lead of the selected type as default
      if (response.data && response.data.length > 0) {
        setSelectedLead(response.data[0]);
      } else {
        setSelectedLead(null);
      }
      
      setError(null);
      console.log(`Successfully fetched ${response.data.length} leads from API`);
    } catch (err) {
      console.error('Error fetching leads:', err);
      console.log('Using mock data instead');
      
      // Use mock data based on leadType
      if (leadType && MOCK_LEADS[leadType]) {
        const mockData = MOCK_LEADS[leadType];
        setLeads(mockData);
        if (mockData.length > 0) {
          setSelectedLead(mockData[0]);
        }
        setError(null);
        console.log(`Using ${mockData.length} mock leads for ${leadType}`);
      } else {
        // Fallback to all mock leads
        const allMockLeads = [...MOCK_LEADS.get, ...MOCK_LEADS.keep, ...MOCK_LEADS.recover];
        setLeads(allMockLeads);
        if (allMockLeads.length > 0) {
          setSelectedLead(allMockLeads[0]);
        }
        setError(null);
        console.log(`Using ${allMockLeads.length} combined mock leads`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch pipeline status options
  const fetchPipelineStatusOptions = async () => {
    setLoadingPipelineStatus(true);
    
    try {
      const authToken = localStorage.getItem('authToken');
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const teamsId = userDetails.teams_id;
      
      if (!teamsId) {
        console.error('No teams_id found in user details');
        setPipelineStatusOptions([]); // Set empty array if no teams_id found
        setLoadingPipelineStatus(false);
        return;
      }
      
      // Make API request to get pipeline status options
      const response = await axios.post(
        'https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/pipeline_status',
        { teams_id: teamsId },
        {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Set pipeline status options
      setPipelineStatusOptions(response.data || []);
      console.log('Fetched pipeline status options:', response.data);
      
    } catch (err) {
      console.error('Error fetching pipeline status options:', err);
      
      // Set fallback pipeline status options in case of error
      setPipelineStatusOptions([
        {
          id: 649,
          title: "Lead Generation",
          description: "The agent identifies potential sellers through referrals, marketing campaigns, online listings, and local networking. Initial contact is made to introduce the agent's services and offer a free property valuation or consultation.",
          group: "seller",
          hex: {
            text: "#00008B",
            background: "#DDEEFF"
          }
        },
        {
          id: 645,
          title: "Comparative Market Analysis",
          description: "Once a seller is interested, the agent assesses the property's market value through a comparative market analysis (CMA).",
          group: "seller",
          hex: {
            text: "#800080",
            background: "#F2E0F7"
          }
        },
        {
          id: 646,
          title: "Mandate",
          description: "Securing the mandate in order to sell or rent the property on behalf of the owner.",
          group: "seller",
          hex: {
            text: "#006400",
            background: "#E0F2E0"
          }
        },
        {
          id: 647,
          title: "Sale Management",
          description: "The agent receives offers from buyers and helps the seller evaluate them based on price, contingencies, and buyer qualifications.",
          group: "seller",
          hex: {
            text: "#008080",
            background: "#E0FFFF"
          }
        },
        {
          id: 648,
          title: "Closing & Follow-Up",
          description: "After accepting an offer, the agent assists the seller with the closing process.",
          group: "buyer",
          hex: {
            text: "#B22222",
            background: "#FFE5E5"
          }
        }
      ]);
    } finally {
      setLoadingPipelineStatus(false);
    }
  };

  // Function to fetch interactions for a specific lead
  const fetchInteractions = async (leadId) => {
    if (!leadId) return;
    
    setLoadingInteractions(true);
    try {
      const authToken = localStorage.getItem('authToken');
      // Use the dynamic apiEndpoint instead of hardcoded URL
      const url = `${apiEndpoint}/interactions?clients_id=${leadId}`;
      
      console.log(`Fetching interactions for lead ${leadId} from:`, url);
      
      const response = await axios.get(url, {
        timeout: 15000, // Increased timeout for slower connections
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });
      
      // Make sure we have a valid response before updating state
      if (response.data && Array.isArray(response.data)) {
        // Sort interactions by created_at in descending order (newest first)
        const sortedInteractions = response.data.sort((a, b) => 
          (b.created_at || 0) - (a.created_at || 0)
        );
        
        setInteractions(sortedInteractions);
        console.log(`Successfully fetched ${sortedInteractions.length} interactions for lead ${leadId}`);
      } else {
        console.warn(`Received invalid interactions data for lead ${leadId}:`, response.data);
        setInteractions([]);
      }
    } catch (err) {
      console.error(`Error fetching interactions:`, err);
      // Use mock data for interactions if API fails
      const mockInteractions = [
        { 
          id: `mock-${leadId}-1`, 
          type: 'call', 
          created_at: Date.now() - 86400000, // yesterday
          comments: 'Initial contact made. Client is interested in discussing options.'
        },
        { 
          id: `mock-${leadId}-2`, 
          type: 'email', 
          created_at: Date.now() - 43200000, // 12 hours ago
          comments: 'Sent follow-up email with property details as requested.'
        }
      ];
      setInteractions(mockInteractions);
      console.log(`Using ${mockInteractions.length} mock interactions for lead ${leadId}`);
    } finally {
      setLoadingInteractions(false);
    }
  };

  // Function to format timestamp
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleLeadSelect = (lead) => {
    // Clear existing interactions before loading new ones
    setInteractions([]);
    setLoadingInteractions(true);
    setSelectedLead(lead);
    // fetchInteractions will be called by the useEffect that watches selectedLead
  };

  // Filter leads based on the active filter
  const filteredLeads = leads.filter(lead => {
    return lead.role.toLowerCase() === typeFilter;
  });

  // Count sellers and buyers
  const sellerCount = leads.filter(lead => lead.role === 'seller').length;
  const buyerCount = leads.filter(lead => lead.role === 'buyer').length;

  // Function to filter leads by type (all, seller, buyer)
  const getFilteredLeads = () => {
    return leads.filter(lead => lead.role.toLowerCase() === typeFilter);
  };

  const renderLeadList = () => {
    if (loading) {
      // Determine which team member's leads we're loading
      let loadingMessage = "Loading leads...";
      if (viewingTeamMemberId) {
        const member = teamMembers.find(m => m.id === viewingTeamMemberId);
        if (member) {
          loadingMessage = `Loading ${member.first_name || member.name}'s leads...`;
        } else {
          loadingMessage = "Loading team member's leads...";
        }
      }
      return <div className="loading">{loadingMessage}</div>;
    }

    if (error) {
      return <div className="error">{error}</div>;
    }

    if (filteredLeads.length === 0) {
      // Determine which team member has no leads
      let noLeadsMessage = "No leads found";
      if (viewingTeamMemberId) {
        const member = teamMembers.find(m => m.id === viewingTeamMemberId);
        if (member) {
          noLeadsMessage = `${member.first_name || member.name} has no ${typeFilter} leads`;
        } else {
          noLeadsMessage = "This team member has no leads";
        }
      } else {
        noLeadsMessage = `You have no ${typeFilter} leads`;
      }
      return <div className="no-leads">{noLeadsMessage}</div>;
    }

    // Handle menu click
    const handleMenuClick = (e, leadId) => {
      e.stopPropagation(); // Prevent lead selection when clicking menu
      if (menuOpen === leadId) {
        setMenuOpen(null); // Close if already open
      } else {
        setMenuOpen(leadId); // Open for this lead
      }
    };

    // Handle actions from menu
    const handleMenuAction = (e, action, lead) => {
      e.stopPropagation(); // Prevent lead selection
      setMenuOpen(null); // Close menu
      
      // Set the lead as selected first
      setSelectedLead(lead);
      
      // Perform the selected action
      switch (action) {
        case 'moveAgent':
          setShowMoveAgentModal(true);
          break;
        case 'moveToGet':
          // Show confirmation for move to Get
          setPendingMoveType('get');
          setShowMoveConfirmModal(true);
          break;
        case 'moveToKeep':
          // Show confirmation for move to Keep
          setPendingMoveType('keep');
          setShowMoveConfirmModal(true);
          break;
        case 'moveToRecover':
          // Show confirmation for move to Recover
          setPendingMoveType('recover');
          setShowMoveConfirmModal(true);
          break;
        case 'convertRole':
          // Convert seller to buyer or vice versa
          convertLeadRole(lead);
          break;
        case 'transfer':
          setShowComingSoonModal(true);
          break;
        case 'delete':
          setShowBinConfirm(true);
          break;
        default:
          break;
      }
    };

    return (
      <div className="lead-list">
        {getFilteredLeads().map(lead => {
          const pipelineStatus = lead.pipeline_status_details || { 
            title: 'Unknown Status',
            hex: { text: '#6c757d', background: '#f8f9fa' }
          };
          
          // Define a color for the status dot based on role or use pipeline color
          const statusColor = lead.role === 'seller' ? '#dc3545' : '#0d6efd';
          const dotColor = pipelineStatus.hex?.text || statusColor;
          
          return (
            <div
              key={lead.id}
              className={`lead-item ${selectedLead && selectedLead.id === lead.id ? 'selected' : ''}`}
              onClick={() => handleLeadSelect(lead)}
            >
              <div className="lead-item-content">
                <div className="lead-name">{lead.name}</div>
                <div className="lead-item-footer">
                  <div className="lead-pipeline-status">
                    <span 
                      className="status-dot" 
                      style={{ backgroundColor: dotColor }}
                    ></span>
                    <span className="status-text">{pipelineStatus.title}</span>
                  </div>
                </div>
              </div>
              
              {/* Menu button */}
              <div 
                className="lead-menu-button" 
                onClick={(e) => handleMenuClick(e, lead.id)}
              >
                <MenuIcon />
              </div>
              
              {/* Dropdown menu */}
              {menuOpen === lead.id && (
                <div className="lead-menu-dropdown">
                  <div className="lead-menu-item" onClick={(e) => handleMenuAction(e, 'moveAgent', lead)}>
                    Move to agent
                  </div>
                  <div className="lead-menu-section">Move to List</div>
                  {leadType !== 'get' && (
                    <div className="lead-menu-item" onClick={(e) => handleMenuAction(e, 'moveToGet', lead)}>
                      Move to Get
                    </div>
                  )}
                  {leadType !== 'keep' && (
                    <div className="lead-menu-item" onClick={(e) => handleMenuAction(e, 'moveToKeep', lead)}>
                      Move to Keep
                    </div>
                  )}
                  {leadType !== 'recover' && (
                    <div className="lead-menu-item" onClick={(e) => handleMenuAction(e, 'moveToRecover', lead)}>
                      Move to Recover
                    </div>
                  )}
                  <div className="lead-menu-section">Convert Role</div>
                  <div className="lead-menu-item" onClick={(e) => handleMenuAction(e, 'convertRole', lead)}>
                    {lead.role === 'seller' ? 'Convert to Buyer' : 'Convert to Seller'}
                  </div>
                  <div className="lead-menu-section">Transfer Options</div>
                  <div className="lead-menu-item" onClick={(e) => handleMenuAction(e, 'transfer', lead)}>
                    Transfer/Share
                  </div>
                  <div className="lead-menu-section">Danger Zone</div>
                  <div className="lead-menu-item lead-menu-delete" onClick={(e) => handleMenuAction(e, 'delete', lead)}>
                    Send to Bin
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderLeadDetails = () => {
    if (!selectedLead) return null;
    
    // Get pipeline status details
    const pipelineStatus = selectedLead.pipeline_status_details || { 
      title: 'No Status',
      hex: { text: '#95A5A6', background: '#F5F5F5' }
    };
    
    return (
      <div className="lead-details">
        <div className="lead-details-header">
          <h2>{selectedLead.name}</h2>
          <span 
            className="pipeline-status-badge" 
            style={{ 
              backgroundColor: pipelineStatus.hex?.background || '#F5F5F5', 
              color: pipelineStatus.hex?.text || '#95A5A6', 
              borderColor: pipelineStatus.hex?.text ? `${pipelineStatus.hex.text}30` : '#E0E0E0' 
            }}
          >
            {pipelineStatus.title}
          </span>
          {!isEditing ? (
            <button 
              className="edit-button"
              onClick={() => {
                setEditedLead({...selectedLead});
                setIsEditing(true);
              }}
            >
              Edit
            </button>
          ) : (
            <div className="edit-actions">
              <button 
                className="save-button"
                onClick={updateLeadDetails}
                disabled={saveProcessing}
              >
                {saveProcessing ? 'Saving...' : 'Save'}
              </button>
              <button 
                className="cancel-button"
                onClick={() => {
                  setIsEditing(false);
                  setEditedLead(null);
                  // Refresh data after canceling
                  fetchLeads();
                  if (selectedLead && selectedLead.id) {
                    fetchInteractions(selectedLead.id);
                  }
                }}
                disabled={saveProcessing}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        <div className="contact-details-box">
          <h3>Contact Details</h3>
          <div className="contact-details-content">
            {/* Name field */}
            <div className="contact-detail-item">
              <div className="contact-detail-label">Name</div>
              {isEditing ? (
                <input 
                  className="contact-detail-input"
                  value={editedLead.name || ''} 
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Name"
                />
              ) : (
                <div className="contact-detail-value">{selectedLead.name}</div>
              )}
            </div>

            {/* Age field */}
            <div className="contact-detail-item">
              <div className="contact-detail-label">Age</div>
              {isEditing ? (
                <input 
                  className="contact-detail-input"
                  type="number"
                  value={editedLead.age || ''} 
                  onChange={(e) => handleInputChange('age', parseInt(e.target.value) || '')}
                  placeholder="Age"
                />
              ) : (
                <div className="contact-detail-value">{selectedLead.age}</div>
              )}
            </div>

            {/* Email field */}
            <div className="contact-detail-item">
              <div className="contact-detail-label">Email</div>
              {isEditing ? (
                <input 
                  className="contact-detail-input"
                  value={editedLead.email || ''} 
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Email"
                />
              ) : (
                <div className="contact-detail-value">{selectedLead.email || "(Not provided)"}</div>
              )}
            </div>

            {/* Gender field */}
            <div className="contact-detail-item">
              <div className="contact-detail-label">Gender</div>
              {isEditing ? (
                <div className="custom-select-wrapper">
                  <select 
                    className="custom-select"
                    value={editedLead.gender || ''} 
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  <div className="custom-select-arrow"></div>
                </div>
              ) : (
                <div className="contact-detail-value">{selectedLead.gender || "(Not provided)"}</div>
              )}
            </div>

            {/* Company field */}
            <div className="contact-detail-item">
              <div className="contact-detail-label">Company</div>
              {isEditing ? (
                <input 
                  className="contact-detail-input"
                  value={editedLead.company || ''} 
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  placeholder="Company"
                />
              ) : (
                <div className="contact-detail-value">{selectedLead.company || "(Not provided)"}</div>
              )}
            </div>

            {/* ID Number field */}
            <div className="contact-detail-item">
              <div className="contact-detail-label">ID Number</div>
              {isEditing ? (
                <input 
                  className="contact-detail-input"
                  value={editedLead.id_number || ''} 
                  onChange={(e) => handleInputChange('id_number', e.target.value)}
                  placeholder="ID Number"
                />
              ) : (
                <div className="contact-detail-value">{selectedLead.id_number || "(Not provided)"}</div>
              )}
            </div>

            {/* Contact Number field */}
            <div className="contact-detail-item">
              <div className="contact-detail-label">Phone</div>
              {isEditing ? (
                <input 
                  className="contact-detail-input"
                  value={editedLead.contact_number || ''} 
                  onChange={(e) => handleInputChange('contact_number', e.target.value)}
                  placeholder="Contact Number"
                />
              ) : (
                <div className="contact-detail-value">
                  {selectedLead.contact_number || "(Not provided)"}
                </div>
              )}
            </div>

            {/* Role field */}
            <div className="contact-detail-item">
              <div className="contact-detail-label">Role</div>
              {isEditing ? (
                <div className="custom-select-wrapper">
                  <select 
                    className="custom-select"
                    value={editedLead.role || ''} 
                    onChange={(e) => handleInputChange('role', e.target.value)}
                  >
                    <option value="">Select Role</option>
                    <option value="buyer">Buyer</option>
                    <option value="seller">Seller</option>
                  </select>
                  <div className="custom-select-arrow"></div>
                </div>
              ) : (
                <div className="contact-detail-value">{selectedLead.role ? selectedLead.role.charAt(0).toUpperCase() + selectedLead.role.slice(1) : "(Not provided)"}</div>
              )}
            </div>

            {/* Type field */}
            <div className="contact-detail-item">
              <div className="contact-detail-label">Type</div>
              {isEditing ? (
                <div className="custom-select-wrapper">
                  <select 
                    className="custom-select"
                    value={editedLead.type || ''} 
                    onChange={(e) => handleInputChange('type', e.target.value)}
                  >
                    <option value="">Select Type</option>
                    <option value="get">Get</option>
                    <option value="keep">Keep</option>
                    <option value="recover">Recover</option>
                  </select>
                  <div className="custom-select-arrow"></div>
                </div>
              ) : (
                <div className="contact-detail-value">{selectedLead.type ? selectedLead.type.charAt(0).toUpperCase() + selectedLead.type.slice(1) : "(Not provided)"}</div>
              )}
            </div>

            {/* Property address field (for sellers) */}
            {selectedLead.role === 'seller' && (
              <div className="contact-detail-item">
                <div className="contact-detail-label">Property</div>
                {isEditing ? (
                  <input 
                    className="contact-detail-input"
                    value={editedLead.seller_property_address || ''} 
                    onChange={(e) => handleInputChange('seller_property_address', e.target.value)}
                    placeholder="Property Address"
                  />
                ) : (
                  <div className="contact-detail-value">{selectedLead.seller_property_address || "(Not provided)"}</div>
                )}
              </div>
            )}

            {/* Property value field (for sellers) */}
            {selectedLead.role === 'seller' && (
              <div className="contact-detail-item">
                <div className="contact-detail-label">Value</div>
                {isEditing ? (
                  <input 
                    className="contact-detail-input"
                    value={editedLead.sellers_property_value || ''} 
                    onChange={(e) => handleInputChange('sellers_property_value', e.target.value)}
                    placeholder="Property Value"
                  />
                ) : (
                  <div className="contact-detail-value">
                    {selectedLead.sellers_property_value ? `R${selectedLead.sellers_property_value}` : "(Not provided)"}
                  </div>
                )}
              </div>
            )}

            {/* Property viewings field (for buyers) */}
            {selectedLead.role === 'buyer' && (
              <div className="contact-detail-item">
                <div className="contact-detail-label">Viewings</div>
                {isEditing ? (
                  <input 
                    className="contact-detail-input"
                    value={Array.isArray(editedLead.buyer_property_viewings) 
                      ? editedLead.buyer_property_viewings.join(', ') 
                      : editedLead.buyer_property_viewings || ''} 
                    onChange={(e) => handleInputChange('buyer_property_viewings', e.target.value.split(',').map(item => item.trim()))}
                    placeholder="Property Viewings (comma separated)"
                  />
                ) : (
                  <div className="contact-detail-value">
                    {Array.isArray(selectedLead.buyer_property_viewings)
                      ? selectedLead.buyer_property_viewings.join(', ')
                      : selectedLead.buyer_property_viewings || "(Not provided)"
                    }
                  </div>
                )}
              </div>
            )}

            {/* Created date - always read-only */}
            <div className="contact-detail-item">
              <div className="contact-detail-label">Created</div>
              <div className="contact-detail-value">{selectedLead.created_at ? formatDate(selectedLead.created_at) : "(Not provided)"}</div>
            </div>

            {/* Recover date field - only show on recover page */}
            {(pageType === 'recover' || selectedLead.type === 'recover') && (
              <div className="contact-detail-item">
                <div className="contact-detail-label">Recover Date</div>
                <div className="contact-detail-value">
                  {selectedLead.recover_date ? formatDate(selectedLead.recover_date) : "(Not provided)"}
                </div>
              </div>
            )}

            {/* Pipeline Status field */}
            <div className="contact-detail-item">
              <div className="contact-detail-label">Pipeline Status</div>
              {isEditing ? (
                loadingPipelineStatus ? (
                  <div className="loading-indicator">Loading pipeline options...</div>
                ) : (
                  <PipelineStatusDropdown
                    options={pipelineStatusOptions}
                    value={editedLead.pipeline_status_id || ''}
                    onChange={(value) => handleInputChange('pipeline_status_id', value)}
                    description={false} /* Set to false to disable description */
                  />
                )
              ) : (
                <div className="contact-detail-value">
                  {selectedLead.pipeline_status_details?.title || 
                   pipelineStatusOptions.find(o => o.id === selectedLead.pipeline_status_id)?.title ? (
                    <div className="pipeline-status-display">
                      <span 
                        className="pipeline-status-badge"
                        style={{
                          backgroundColor: selectedLead.pipeline_status_details?.hex?.background || 
                            pipelineStatusOptions.find(o => o.id === selectedLead.pipeline_status_id)?.hex?.background ||
                            '#F5F5F5',
                          color: selectedLead.pipeline_status_details?.hex?.text ||
                            pipelineStatusOptions.find(o => o.id === selectedLead.pipeline_status_id)?.hex?.text ||
                            '#333333'
                        }}
                      >
                        {selectedLead.pipeline_status_details?.title || 
                         pipelineStatusOptions.find(o => o.id === selectedLead.pipeline_status_id)?.title}
                      </span>
                    </div>
                  ) : (
                    "Not specified"
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Profile Info Section - Removed */}
      </div>
    );
  };

  // Function to add a new interaction/note
  const addNote = async () => {
    if (!selectedLead || !noteText.trim()) return;
    
    setSavingNote(true);
    
    try {
      const authToken = localStorage.getItem('authToken');
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      
      const noteData = {
        id: 0, // 0 for new notes
        timestamp: null, // null for new notes
        comments: noteText.trim(),
        clients_id: selectedLead.id,
        rep_id: userDetails.id || 0,
        teams_id: userDetails.teams_id || 0,
        type: selectedNoteType
      };
      
      const response = await axios.post(
        'https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/interactions',
        noteData,
        {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Note added successfully:', response.data);
      
      // Clear the note input
      setNoteText('');
      
      // Hide the note input box
      setEditingNote(null);
      
      // Refresh the notes
      fetchInteractions(selectedLead.id);
      
      // Show success message
      showSuccess('Note added successfully');
      
    } catch (err) {
      console.error('Error adding note:', err);
      alert('Failed to add note. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };
  
  // Function to update an existing note
  const updateNote = async () => {
    if (!editingNote || !noteText.trim()) return;
    
    setSavingNote(true);
    
    try {
      const authToken = localStorage.getItem('authToken');
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      
      const noteData = {
        ...editingNote,
        comments: noteText.trim(),
        rep_id: editingNote.rep_id || userDetails.id || 0,
        teams_id: editingNote.teams_id || userDetails.teams_id || 0
      };
      
      const response = await axios.post(
        'https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/interactions',
        noteData,
        {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Note updated successfully:', response.data);
      
      // Clear the editing state
      setEditingNote(null);
      setNoteText('');
      
      // Refresh the notes
      fetchInteractions(selectedLead.id);
      
      // Show success message
      showSuccess('Note updated successfully');
      
    } catch (err) {
      console.error('Error updating note:', err);
      alert('Failed to update note. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };
  
  // Function to handle edit note click
  const handleEditNote = (note) => {
    setEditingNote(note);
    setNoteText(note.comments || '');
    setSelectedNoteType(note.type || 'note');
  };
  
  // Function to cancel note editing
  const cancelEditNote = () => {
    setEditingNote(null);
    setNoteText('');
  };

  // Function to delete a note
  const deleteNote = async (noteId) => {
    if (!noteId) return;
    
    // Show processing state
    setSavingNote(true);
    
    try {
      const authToken = localStorage.getItem('authToken');
      
      const response = await axios.delete(
        `https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/interactions/${noteId}`,
        {
          headers: {
            'Authorization': authToken
          }
        }
      );
      
      console.log('Note deleted successfully:', response.data);
      
      // Refresh the notes
      fetchInteractions(selectedLead.id);
      
      // Show success message
      showSuccess('Note deleted successfully');
      
      // Close confirmation dialog
      setConfirmDeleteNote(false);
      
    } catch (err) {
      console.error('Error deleting note:', err);
      alert('Failed to delete note. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };
  
  // Function to handle delete button click - shows confirmation dialog
  const handleDeleteNote = (noteId) => {
    setDeletingNoteId(noteId);
    setConfirmDeleteNote(true);
  };
  
  // Function to cancel note deletion
  const cancelDeleteNote = () => {
    setDeletingNoteId(null);
    setConfirmDeleteNote(false);
  };

  // Add effect to handle clicks outside the note editor
  useEffect(() => {
    function handleClickOutside(event) {
      // Only handle this if we're in add/edit note mode and user didn't click in note editor
      if (editingNote && noteEditorRef.current && !noteEditorRef.current.contains(event.target)) {
        // If there's text in the note, ask for confirmation before closing
        if (noteText.trim()) {
          if (window.confirm('Discard your unsaved note?')) {
            setEditingNote(null);
            setNoteText('');
          }
        } else {
          // If no text, just close without confirmation
          setEditingNote(null);
        }
      }
    }
    
    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingNote, noteText]);

  // Modify the renderLeadNotes function to use the ref
  const renderLeadNotes = () => {
    // Only return null if there's no selected lead
    if (!selectedLead) return null;

    // Function to get icon for note category or type
    const getNoteIcon = (type) => {
      if (!type) return '📝';
      
      const typeStr = String(type).toLowerCase();
      switch(typeStr) {
        case 'call': return '📞';
        case 'email': return '📧';
        case 'meeting': return '🤝';
        case 'consultation': return '📋';
        default: return '📝';
      }
    };

    // Safe check for date formatting
    const safeFormatDate = (timestamp) => {
      try {
        if (!timestamp) return 'Date not available';
        return formatDate(timestamp);
      } catch (err) {
        console.error('Error formatting date:', err);
        return 'Invalid date';
      }
    };

    return (
      <div className="lead-notes">
        <div className="lead-notes-header">
          <h3>Notes</h3>
        </div>

        {/* Only show note input when editing or when explicitly adding a new note */}
        {editingNote && (
          <div className="add-note-section" ref={noteEditorRef}>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder={editingNote.id ? "Edit your note..." : "Add a new note..."}
              autoFocus
            />
            
            <div className="note-action-buttons">
              <button 
                className="cancel-button"
                onClick={cancelEditNote}
                disabled={savingNote}
              >
                Cancel
              </button>
              <button 
                className="add-note-button"
                onClick={editingNote.id ? updateNote : addNote}
                disabled={!noteText.trim() || savingNote}
              >
                {savingNote ? 'Saving...' : (editingNote.id ? 'Update' : 'Add')}
              </button>
            </div>
          </div>
        )}

        <div className="notes-container">
          {!editingNote && (
            <button 
              className="add-new-note-button"
              onClick={() => {
                setEditingNote({type: 'note', id: 0});
                setNoteText('');
              }}
            >
              Add Note
            </button>
          )}
          
          {loadingInteractions ? (
            <div className="loading">Loading notes...</div>
          ) : interactions.length === 0 ? (
            <div className="no-notes">No notes available for this {selectedLead.role || 'contact'}.</div>
          ) : (
            interactions.map(interaction => {
              // Skip rendering if interaction is invalid
              if (!interaction || typeof interaction !== 'object') return null;
              
              return (
                <div key={interaction.id || `note-${Math.random()}`} className="note-item">
                  <div className="note-header">
                    <span className="note-icon">{getNoteIcon(interaction.type)}</span>
                    <div className="note-date">{safeFormatDate(interaction.created_at)}</div>
                    <div className="note-actions">
                      <div className="note-edit-button" onClick={() => handleEditNote(interaction)}>
                        Edit
                      </div>
                      <div className="note-delete-button" onClick={() => handleDeleteNote(interaction.id)}>
                        Remove
                      </div>
                    </div>
                  </div>
                  <div className="note-content">{interaction.comments || 'No comment provided'}</div>
                </div>
              );
            })
          )}
        </div>
        
        {/* Confirmation dialog for note deletion */}
        {confirmDeleteNote && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Delete Note</h3>
                <button className="modal-close" onClick={cancelDeleteNote}>&times;</button>
              </div>
              <div className="modal-body">
                <p className="delete-warning">Are you sure you want to delete this note?</p>
                <p>This action cannot be undone.</p>
              </div>
              <div className="modal-footer delete-actions">
                <button 
                  className="cancel-btn" 
                  onClick={cancelDeleteNote}
                  disabled={savingNote}
                >
                  Cancel
                </button>
                <button 
                  className="delete-btn" 
                  onClick={() => deleteNote(deletingNoteId)}
                  disabled={deletingNoteId === null || savingNote}
                >
                  {savingNote ? (
                    <>
                      <span className="spinner"></span>
                      Deleting...
                    </>
                  ) : 'Delete Note'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Function to handle team member selection
  const handleTeamMemberSelect = (memberId) => {
    setSelectedTeamMember(memberId === selectedTeamMember ? null : memberId);
  };

  // Function to fetch team members from the API
  const fetchTeamMembers = async () => {
    setLoadingTeamMembers(true);
    setTeamMembersError(null);
    
    try {
      // Get user details from localStorage
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const teamsId = userDetails?.teams_id;
      const authToken = localStorage.getItem('authToken');
      
      if (!teamsId) {
        console.error('No teams_id found in user details:', userDetails);
        setTeamMembersError('Missing team information. Please contact support.');
        setLoadingTeamMembers(false);
        return;
      }
      
      if (!authToken) {
        console.error('No auth token found');
        setTeamMembersError('Authentication error. Please log in again.');
        setLoadingTeamMembers(false);
        return;
      }
      
      // URL for the API
      const url = `https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/users?team_id=${teamsId}`;
      
      console.log('Fetching team members from URL:', url);
      
      // Make the GET request
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch team members');
      }
      
      const data = await response.json();
      console.log('Fetched team members:', data);
      
      setTeamMembers(data);
    } catch (err) {
      console.error('Error fetching team members:', err);
      setTeamMembersError('Failed to load team members. Please try again later.');
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  // Helper function to extract profile image URL
  const getProfileImageUrl = (profileData) => {
    if (!profileData) return null;
    
    // Check direct URL
    if (typeof profileData === 'string') return profileData;
    
    // Check common formats
    if (profileData.url) return profileData.url;
    if (profileData.picture?.url) return profileData.picture.url;
    if (profileData.image?.url) return profileData.image.url;
    if (profileData.profile_pic?.url) return profileData.profile_pic.url;
    
    // For nested structures
    for (const key in profileData) {
      if (profileData[key] && typeof profileData[key] === 'object') {
        if (profileData[key].url) return profileData[key].url;
      }
    }
    
    return null;
  };

  // Helper function to show success modal
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessModal(true);
    
    // Automatically hide the success modal after 2 seconds
    setTimeout(() => {
      setShowSuccessModal(false);
    }, 2000);
  };

  // Function to move lead to a different agent
  const moveLeadToAgent = async () => {
    if (!selectedLead || !selectedTeamMember) {
      alert('Please select a team member to move the lead to.');
      return;
    }
    
    try {
      const authToken = localStorage.getItem('authToken');
      
      // Use the same API endpoint as the other move operations
      // but change the user_id to the selected team member's ID
      const lead = { ...selectedLead, user_id: selectedTeamMember };
      await axios.post('https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/clients', lead, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });
      
      // Refresh leads to show the update
      fetchLeads();
      
      // Reset and close modal
      setSelectedTeamMember(null);
      setShowMoveAgentModal(false);
      
      // Show success message
      const teamMember = teamMembers.find(member => member.id === selectedTeamMember);
      const memberName = teamMember ? (teamMember.first_name || teamMember.name) : 'team member';
      showSuccess(`${selectedLead.name} has been successfully moved to ${memberName}`);
      
    } catch (err) {
      console.error('Error moving lead to agent:', err);
      alert('Failed to move lead. Please try again.');
    }
  };

  // When the user clicks "Move to agent", fetch team members
  useEffect(() => {
    if (showMoveAgentModal) {
      fetchTeamMembers();
    } else {
      // Reset selection when modal closes
      setSelectedTeamMember(null);
    }
  }, [showMoveAgentModal]);

  // Function to move lead to different list (get/keep/recover)
  const moveLeadToList = async (leadId, listType) => {
    try {
      const authToken = localStorage.getItem('authToken');
      
      // Use the new API endpoint for all list types
      const lead = { ...selectedLead, type: listType };
      await axios.post('https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/clients', lead, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });
      
      // If moving to a different list than current, refresh leads
      if (listType !== leadType) {
        fetchLeads();
      }
      
      // Close confirmation modal
      setShowMoveConfirmModal(false);
      setShowMoveListModal(false);
      
      // Show success message
      const listTypeNames = {
        get: 'Get Leads',
        keep: 'Keep',
        recover: 'Recover'
      };
      
      showSuccess(`${selectedLead.name} has been successfully moved to ${listTypeNames[listType]}`);
      
    } catch (err) {
      console.error('Error moving lead to list:', err);
      alert('Failed to move lead. Please try again.');
    }
  };
  
  // Function to delete a lead
  const deleteLead = async (leadId) => {
    try {
      const authToken = localStorage.getItem('authToken');
      
      // Use the specified API endpoint - create a special case for delete
      // Since DELETE requests typically don't have a body, we'll use a POST with a special field
      const lead = { ...selectedLead, _delete: true };
      
      await axios.post('https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/clients', lead, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });
      
      // Store the name before removing from state
      const leadName = selectedLead ? selectedLead.name : 'Lead';
      
      // Remove lead from local state
      setLeads(prevLeads => prevLeads.filter(lead => lead.id !== leadId));
      
      // If deleted lead was selected, clear selection
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead(null);
      }
      
      setShowDeleteConfirm(false);
      
      // Show success message
      showSuccess(`${leadName} has been successfully deleted`);
      
    } catch (err) {
      console.error('Error deleting lead:', err);
      alert('Failed to delete lead. Please try again.');
    }
  };
  
  // Function to transfer lead to a different branch
  const transferLeadToBranch = async (leadId, branchId, isShared = false) => {
    try {
      const authToken = localStorage.getItem('authToken');
      
      // Use the specified API endpoint with branch_id and shared properties
      const lead = { 
        ...selectedLead, 
        branch_id: branchId, 
        shared: isShared // true for sharing, false for complete transfer
      };
      
      await axios.post('https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/clients', lead, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });
      
      // Get branch name for success message
      const branch = branches.find(b => b.id === branchId);
      const branchName = branch ? branch.name : 'branch';
      
      // If not shared (complete transfer), remove from current list
      if (!isShared) {
        setLeads(prevLeads => prevLeads.filter(lead => lead.id !== leadId));
        
        // If transferred lead was selected, clear selection
        if (selectedLead && selectedLead.id === leadId) {
          setSelectedLead(null);
        }
      }
      
      setShowTransferModal(false);
      
      // Show success message
      showSuccess(`${selectedLead.name} has been successfully ${isShared ? 'shared with' : 'transferred to'} ${branchName}`);
      
    } catch (err) {
      console.error('Error transferring lead:', err);
      alert('Failed to transfer lead. Please try again.');
    }
  };
  
  // Function to fetch agents list (for transfer)
  const fetchAgents = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await axios.get(`${apiEndpoint}/agents`, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });
      
      setAgents(response.data);
    } catch (err) {
      console.error('Error fetching agents:', err);
      // Use mock data if API fails
      setAgents([
        { id: 101, name: 'John Smith' },
        { id: 102, name: 'Sarah Johnson' },
        { id: 103, name: 'Michael Brown' },
      ]);
    }
  };
  
  // Function to fetch branches list (for transfer)
  const fetchBranches = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await axios.get(`${apiEndpoint}/branches`, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });
      
      setBranches(response.data);
    } catch (err) {
      console.error('Error fetching branches:', err);
      // Use mock data if API fails
      setBranches([
        { id: 201, name: 'Downtown Office' },
        { id: 202, name: 'Westside Branch' },
        { id: 203, name: 'North County' },
      ]);
    }
  };

  // Toggle team member selector dropdown
  const toggleTeamMemberSelector = () => {
    // Only toggle if user has sufficient role level
    if (userRoleLevel > 1) {
      setShowTeamMemberSelector(!showTeamMemberSelector);
      
      // If we're opening the selector and don't have team members yet, fetch them
      if (!showTeamMemberSelector && teamMembers.length === 0) {
        fetchTeamMembers();
      }
    }
  };
  
  // Handle selecting a team member's leads to view
  const handleViewTeamMemberLeads = (memberId) => {
    // Only allow viewing other team members' leads if user has sufficient role level
    if (userRoleLevel > 1) {
      setViewingTeamMemberId(memberId === currentUserId ? null : memberId);
      setShowTeamMemberSelector(false);
    }
  };
  
  // Get the name of the currently viewed team member
  const getCurrentViewName = () => {
    if (!viewingTeamMemberId) {
      return 'My Leads';
    }
    
    const member = teamMembers.find(m => m.id === viewingTeamMemberId);
    return member ? `${member.first_name || member.name}'s Leads` : 'Team Member Leads';
  };

  // Modal components for lead management actions
  const renderModals = () => {
    if (!selectedLead) return null;
    
    return (
      <>
        {/* Success Modal */}
        {showSuccessModal && (
          <div className="modal-overlay success-overlay" onClick={() => setShowSuccessModal(false)}>
            <div className="modal-content success-modal" onClick={e => e.stopPropagation()}>
              <div className="success-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="32" cy="32" r="32" fill="#147b66" fillOpacity="0.1"/>
                  <path d="M28 38.17L44.17 22L47 24.83L28 43.83L17 32.83L19.83 30L28 38.17Z" fill="#147b66"/>
                </svg>
              </div>
              <h3>Success!</h3>
              <p>{successMessage}</p>
            </div>
          </div>
        )}

        {/* Coming Soon Modal */}
        {showComingSoonModal && (
          <div className="modal-overlay" onClick={() => setShowComingSoonModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Coming Soon</h3>
                <button className="modal-close" onClick={() => setShowComingSoonModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="coming-soon-icon">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="32" fill="#3498DB" fillOpacity="0.1"/>
                    <path d="M32 16C23.2 16 16 23.2 16 32C16 40.8 23.2 48 32 48C40.8 48 48 40.8 48 32C48 23.2 40.8 16 32 16ZM32 44C25.4 44 20 38.6 20 32C20 25.4 25.4 20 32 20C38.6 20 44 25.4 44 32C44 38.6 38.6 44 32 44ZM33 22H30V34L40.5 40.3L42 37.8L33 32.6V22Z" fill="#3498DB"/>
                  </svg>
                </div>
                <h4 style={{ textAlign: 'center', color: '#3498DB', marginBottom: '16px' }}>Transfer/Share Feature</h4>
                <p>The Transfer/Share feature is currently under development and will be available soon.</p>
                <p style={{ marginTop: '10px' }}>This feature will allow you to transfer or share {selectedLead.role}s with other branches for referrals.</p>
              </div>
              <div className="modal-footer">
                <button 
                  onClick={() => setShowComingSoonModal(false)}
                  style={{ 
                    backgroundColor: '#3498DB', 
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Move Confirmation Modal */}
        {showMoveConfirmModal && (
          <div className="modal-overlay" onClick={() => setShowMoveConfirmModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Confirm Move</h3>
                <button className="modal-close" onClick={() => setShowMoveConfirmModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to move <strong>{selectedLead.name}</strong> to the <strong>{pendingMoveType.charAt(0).toUpperCase() + pendingMoveType.slice(1)}</strong> list?</p>
              </div>
              <div className="modal-footer">
                <button onClick={() => setShowMoveConfirmModal(false)}>Cancel</button>
                <button 
                  onClick={() => moveLeadToList(selectedLead.id, pendingMoveType)}
                  style={{ 
                    backgroundColor: '#147b66', 
                    color: 'white',
                    border: 'none',
                    marginLeft: '10px',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Move to Agent Modal - Updated with team members grid */}
        {showMoveAgentModal && (
          <div className="modal-overlay" onClick={() => setShowMoveAgentModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '600px', maxWidth: '90%' }}>
              <div className="modal-header">
                <h3>Move Lead to Team Member</h3>
                <button className="modal-close" onClick={() => setShowMoveAgentModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <p>Select a team member to assign <strong>{selectedLead.name}</strong> to:</p>
                
                {loadingTeamMembers ? (
                  <div className="loading-indicator">Loading team members...</div>
                ) : teamMembersError ? (
                  <div className="error-message">{teamMembersError}</div>
                ) : teamMembers.length === 0 ? (
                  <div className="no-members">No team members found</div>
                ) : (
                  <div className="member-selection-grid">
                    {teamMembers.map(member => (
                      <div 
                        key={member.id} 
                        className={`member-selection-card ${selectedTeamMember === member.id ? 'selected' : ''}`}
                        onClick={() => handleTeamMemberSelect(member.id)}
                      >
                        <div className="member-select-avatar">
                          {member.profile_picture ? (
                            <img 
                              src={getProfileImageUrl(member.profile_picture)} 
                              alt={member.first_name || member.name} 
                            />
                          ) : (
                            <div className="member-initial">{(member.first_name || member.name)?.charAt(0) || '?'}</div>
                          )}
                        </div>
                        <div className="member-select-info">
                          <h3 className="member-name">{member.first_name || member.name}</h3>
                          <p className="member-role">{member.role_title || 'Team Member'}</p>
                        </div>
                        {selectedTeamMember === member.id && (
                          <div className="member-selected-indicator"></div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button onClick={() => setShowMoveAgentModal(false)}>Cancel</button>
                <button 
                  onClick={moveLeadToAgent}
                  disabled={!selectedTeamMember}
                  style={{ 
                    backgroundColor: '#147b66', 
                    color: 'white',
                    border: 'none',
                    marginLeft: '10px',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: selectedTeamMember ? 'pointer' : 'not-allowed',
                    opacity: selectedTeamMember ? 1 : 0.6
                  }}
                >
                  Move Lead
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Move to List Modal */}
        {showMoveListModal && (
          <div className="modal-overlay" onClick={() => setShowMoveListModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Move Lead to List</h3>
                <button className="modal-close" onClick={() => setShowMoveListModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <p>Select a list to move <strong>{selectedLead.name}</strong> to:</p>
                <div className="list-options">
                  {leadType !== 'get' && (
                    <div 
                      className="list-option"
                      onClick={() => moveLeadToList(selectedLead.id, 'get')}
                    >
                      <h4>Get</h4>
                      <p>Active leads in acquisition phase</p>
                    </div>
                  )}
                  {leadType !== 'keep' && (
                    <div 
                      className="list-option"
                      onClick={() => moveLeadToList(selectedLead.id, 'keep')}
                    >
                      <h4>Keep</h4>
                      <p>Current clients and active relationships</p>
                    </div>
                  )}
                  {leadType !== 'recover' && (
                    <div 
                      className="list-option"
                      onClick={() => moveLeadToList(selectedLead.id, 'recover')}
                    >
                      <h4>Recover</h4>
                      <p>Past clients and relationships to rekindle</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={() => setShowMoveListModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
        
        {/* Transfer/Share Modal */}
        {showTransferModal && (
          <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Transfer/Share Lead</h3>
                <button className="modal-close" onClick={() => setShowTransferModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <p>Select a branch to transfer <strong>{selectedLead.name}</strong> to:</p>
                <div className="branch-list">
                  {branches.length > 0 ? (
                    branches.map(branch => (
                      <div key={branch.id} className="branch-item">
                        <h4>{branch.name}</h4>
                        <div className="branch-actions">
                          <button onClick={() => transferLeadToBranch(selectedLead.id, branch.id, false)}>
                            Transfer
                          </button>
                          <button onClick={() => transferLeadToBranch(selectedLead.id, branch.id, true)}>
                            Share
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="loading">Loading branches...</div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={() => setShowTransferModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
        
        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Confirm Deletion</h3>
                <button className="modal-close" onClick={() => setShowDeleteConfirm(false)}>×</button>
              </div>
              <div className="modal-body">
                <p className="delete-warning">
                  Are you sure you want to delete <strong>{selectedLead.name}</strong>?
                </p>
                <p>This action cannot be undone. All data associated with this lead will be permanently removed.</p>
              </div>
              <div className="modal-footer delete-actions">
                <button className="cancel-btn" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </button>
                <button className="delete-btn" onClick={() => deleteLead(selectedLead.id)}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bin Confirmation Modal */}
        {showBinConfirm && (
          <div className="modal-overlay" onClick={() => setShowBinConfirm(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Send to Bin</h3>
                <button className="modal-close" onClick={() => setShowBinConfirm(false)}>×</button>
              </div>
              <div className="modal-body">
                <p className="delete-warning">
                  Send <strong>{selectedLead.name}</strong> to bin?
                </p>
                <p>This lead will be moved to the bin and automatically deleted after 30 days.</p>
              </div>
              <div className="modal-footer delete-actions">
                <button className="cancel-btn" onClick={() => setShowBinConfirm(false)}>
                  Cancel
                </button>
                <button 
                  className="delete-btn" 
                  onClick={() => sendLeadToBin(selectedLead.id)}
                  disabled={sendingToBin}
                >
                  {sendingToBin ? (
                    <>
                      <span className="spinner"></span>
                      Processing...
                    </>
                  ) : 'Send to Bin'}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Function to convert lead role (seller to buyer or vice versa)
  const convertLeadRole = async (lead) => {
    try {
      const authToken = localStorage.getItem('authToken');
      
      // Determine the new role
      const newRole = lead.role === 'seller' ? 'buyer' : 'seller';
      
      // Create a copy of the lead with the updated role
      const updatedLead = { ...lead, role: newRole };
      
      // Send the update to the API
      await axios.post('https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/clients', updatedLead, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });
      
      // Update the lead locally in the state
      setLeads(prevLeads => 
        prevLeads.map(l => l.id === lead.id ? { ...l, role: newRole } : l)
      );
      
      // Update the selected lead if it's the converted one
      if (selectedLead && selectedLead.id === lead.id) {
        setSelectedLead({ ...selectedLead, role: newRole });
      }
      
      // Show success message
      const fromRole = lead.role.charAt(0).toUpperCase() + lead.role.slice(1);
      const toRole = newRole.charAt(0).toUpperCase() + newRole.slice(1);
      showSuccess(`${lead.name} has been converted from ${fromRole} to ${toRole}`);
      
    } catch (err) {
      console.error('Error converting lead role:', err);
      alert('Failed to convert lead. Please try again.');
    }
  };

  // Function to update lead details
  const updateLeadDetails = async () => {
    if (!editedLead) return;
    
    // Show processing state
    setSaveProcessing(true);
    
    try {
      const authToken = localStorage.getItem('authToken');
      
      // Use the same endpoint as other client updates
      await axios.post('https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/clients', editedLead, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });
      
      // Update the lead in local state
      setLeads(prevLeads => 
        prevLeads.map(lead => lead.id === editedLead.id ? editedLead : lead)
      );
      
      // Update the selected lead
      setSelectedLead(editedLead);
      
      // Exit edit mode
      setIsEditing(false);
      
      // Refresh all data
      fetchLeads();
      if (editedLead.id) {
        fetchInteractions(editedLead.id);
      }
      
      // Show success message
      showSuccess(`${editedLead.name}'s details have been successfully updated`);
      
    } catch (err) {
      console.error('Error updating lead details:', err);
      alert('Failed to update lead details. Please try again.');
    } finally {
      // Hide processing state
      setSaveProcessing(false);
    }
  };

  // Function to handle input changes
  const handleInputChange = (field, value) => {
    setEditedLead(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Reset editing state when selected lead changes
  useEffect(() => {
    setIsEditing(false);
    setEditedLead(null);
  }, [selectedLead]);

  // Function to send a lead to bin
  const sendLeadToBin = async (leadId) => {
    if (!leadId) return;
    
    setSendingToBin(true);
    
    try {
      const authToken = localStorage.getItem('authToken');
      
      // Call the bin API
      const response = await axios.post(
        'https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/sent_to_bin',
        { clients_id: leadId },
        {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Lead sent to bin successfully:', response.data);
      
      // Store the name before removing from state
      const leadName = selectedLead ? selectedLead.name : 'Lead';
      
      // Remove lead from local state
      setLeads(prevLeads => prevLeads.filter(lead => lead.id !== leadId));
      
      // If binned lead was selected, clear selection
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead(null);
      }
      
      setShowBinConfirm(false);
      
      // Show success message
      showSuccess(`${leadName} has been sent to bin and will be automatically deleted after 30 days`);
      
    } catch (err) {
      console.error('Error sending lead to bin:', err);
      alert('Failed to send lead to bin. Please try again.');
    } finally {
      setSendingToBin(false);
    }
  };

  return (
    <div className={`lead-crm-container ${viewingTeamMemberId ? 'viewing-team-member' : ''}`}>
      <div className="lead-header">
        <div className="header-content">
          <h1>
            {leadType === 'get' && 'Get Leads'}
            {leadType === 'keep' && 'Keep Leads'}
            {leadType === 'recover' && 'Recover Leads'}
          </h1>
          {/* Debug info - remove after fixing */}
          <small style={{color: 'gray', fontSize: '10px'}}>
            userRoleLevel: {userRoleLevel} (type: {typeof userRoleLevel})
          </small>
        </div>
      </div>
      
      <div className="lead-content">
        <div className="lead-sidebar">
          {userRoleLevel > 1 && (
            <div className={`team-view-selector ${showTeamMemberSelector ? 'open' : ''}`}>
              <button 
                className="team-view-button"
                onClick={toggleTeamMemberSelector}
              >
                {getCurrentViewName()}
                <svg className="dropdown-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              {showTeamMemberSelector && (
                <div className="team-member-dropdown">
                  <div 
                    className={`team-member-option ${!viewingTeamMemberId ? 'active' : ''}`}
                    onClick={() => handleViewTeamMemberLeads(currentUserId)}
                  >
                    My Leads
                  </div>
                  
                  {loadingTeamMembers ? (
                    <div className="loading-team-members">Loading team members...</div>
                  ) : teamMembersError ? (
                    <div className="team-members-error">{teamMembersError}</div>
                  ) : (
                    teamMembers
                      .filter(member => member.id !== currentUserId) // Filter out current user
                      .map(member => (
                        <div 
                          key={member.id}
                          className={`team-member-option ${viewingTeamMemberId === member.id ? 'active' : ''}`}
                          onClick={() => handleViewTeamMemberLeads(member.id)}
                        >
                          {member.first_name || member.name}'s Leads
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className="filter-tabs">
            <button
              className={`filter-tab ${typeFilter === "seller" ? "active" : ""}`}
              onClick={() => setTypeFilter("seller")}
            >
              Sellers ({sellerCount})
            </button>
            <button
              className={`filter-tab ${typeFilter === "buyer" ? "active" : ""}`}
              onClick={() => setTypeFilter("buyer")}
            >
              Buyers ({buyerCount})
            </button>
          </div>
          {renderLeadList()}
        </div>
        <div className="lead-details-column">
          {renderLeadDetails()}
        </div>
        <div className="lead-notes-column">
          {renderLeadNotes()}
        </div>
      </div>
      
      {/* Render all modals */}
      {renderModals()}
      
      {/* Click catcher to close menus when clicking outside */}
      {menuOpen && (
        <div 
          className="click-catcher" 
          onClick={() => setMenuOpen(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 5
          }}
        />
      )}
      
      {/* Click catcher to close team member selector when clicking outside */}
      {showTeamMemberSelector && userRoleLevel > 1 && (
        <div 
          className="click-catcher" 
          onClick={() => setShowTeamMemberSelector(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9
          }}
        />
      )}
    </div>
  );
}

export default ClientCRM; 